import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { MultiPlanNudgeToast, MultiPlanSuccessToast } from "./MultiPlanNudgeToast";
import { MultiPlanGuideModal, SelectedPlansByGroup } from "./MultiPlanGuideModal";
import { RecommendedPlanGroup, ResolvedRecommendedPlan } from "./MultiPlanGuideUtils";

const SUCCESS_TOAST_DURATION_MS = 4000;

interface MultiPlanGuideProps {
    isNudgeVisible: boolean;
    /** terminal for the session — hides the nudge and prevents re-shows */
    dismissNudge: () => void;
    groups: RecommendedPlanGroup[] | null;
    /** applies the plan's coverage settings to the group's pets (CoverageEditor owns the write path) */
    onApplyPlan: (group: RecommendedPlanGroup, plan: ResolvedRecommendedPlan) => Promise<void>;
}

export const MultiPlanGuide: React.FC<MultiPlanGuideProps> = ({ isNudgeVisible, dismissNudge, groups, onApplyPlan }) => {
    const { updateAppState } = useAppLayerContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlans, setSelectedPlans] = useState<SelectedPlansByGroup>({});
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    // Selections whose quote update actually succeeded. Kept separate from the optimistic
    // `selectedPlans` so the success toast can never announce an apply that failed.
    const [appliedPlans, setAppliedPlans] = useState<SelectedPlansByGroup>({});

    // One plan applied to every pet (the linked "N pets" group) still counts as one plan,
    // so the success copy stays singular; only per-pet groups can push this above 1.
    const appliedPlanCount = Object.values(appliedPlans).filter(Boolean).length;
    const hasAppliedPlan = appliedPlanCount > 0;

    // An apply is the quote update it awaits — the PUT and, on a multi-pet quote, the stored-quote
    // read-back behind it (see useQuote) — so it commonly outlives a quick Done. Refs rather than
    // state: the apply's own continuation decides whether a close is still waiting on it, and that
    // closure predates the click.
    const pendingApplyCountRef = useRef(0);
    const isCloseAwaitingSettleRef = useRef(false);
    const hasAppliedPlanRef = useRef(hasAppliedPlan);
    useEffect(() => {
        hasAppliedPlanRef.current = hasAppliedPlan;
    }, [hasAppliedPlan]);

    // Opening the guide is not terminal for the nudge and does not hide it — it stays on the
    // page behind the modal (z-45 vs the dialog's z-50, so it is dimmed by the overlay and
    // non-interactive) and is simply still there when the modal closes. Only "No thanks" or a
    // successful apply retires it.
    const openGuide = useCallback(() => {
        setIsModalOpen(true);
        // Reopened before a close settled: that close's confirmation is void, the next close makes it.
        isCloseAwaitingSettleRef.current = false;
    }, []);

    // D5: the confirmation shows once the modal has closed with a plan confirmed applied — a late
    // success still confirms, a late failure (rolled back upstream) stays silent.
    const confirmClose = useCallback(
        (hasApplied: boolean) => {
            isCloseAwaitingSettleRef.current = false;
            if (hasApplied) {
                dismissNudge();
                setShowSuccessToast(true);
            }
        },
        [dismissNudge]
    );

    const closeGuide = useCallback(() => {
        setIsModalOpen(false);
        if (pendingApplyCountRef.current > 0) {
            // `appliedPlans` cannot know about an apply still in flight; the last one to settle confirms.
            isCloseAwaitingSettleRef.current = true;
            return;
        }
        confirmClose(hasAppliedPlan);
    }, [hasAppliedPlan, confirmClose]);

    // D4: optimistic select — the button flips to "Selected" immediately while the
    // apply runs in the background. On failure the selection rolls back, which also
    // re-expands a collapsed group (its collapsed state requires a selected plan).
    const handleSelectPlan = useCallback(
        async (group: RecommendedPlanGroup, plan: ResolvedRecommendedPlan) => {
            setSelectedPlans(previous => ({ ...previous, [group.groupKey]: plan.id }));
            pendingApplyCountRef.current += 1;
            let isApplied = false;
            try {
                await onApplyPlan(group, plan);
                setAppliedPlans(previous => ({ ...previous, [group.groupKey]: plan.id }));
                isApplied = true;
            } catch (error) {
                console.error("Failed to apply recommended plan", error);
                // Only roll back if this plan is still the group's selection — a newer
                // selection that already succeeded must not be cleared by a late rejection.
                setSelectedPlans(previous => (previous[group.groupKey] === plan.id ? { ...previous, [group.groupKey]: undefined } : previous));
                setAppliedPlans(previous => (previous[group.groupKey] === plan.id ? { ...previous, [group.groupKey]: undefined } : previous));
            } finally {
                pendingApplyCountRef.current -= 1;
                if (pendingApplyCountRef.current === 0 && isCloseAwaitingSettleRef.current) {
                    // This apply's `setAppliedPlans` is still queued, so its outcome is counted from here.
                    confirmClose(isApplied || hasAppliedPlanRef.current);
                }
            }
        },
        [onApplyPlan, confirmClose]
    );

    useEffect(() => {
        if (!showSuccessToast) return;
        const timer = setTimeout(() => setShowSuccessToast(false), SUCCESS_TOAST_DURATION_MS);
        return () => clearTimeout(timer);
    }, [showSuccessToast]);

    // Let the form know one of our bottom toasts is on screen so a bottom-positioned
    // gift-card toast hides instead of overlapping it (formController reads this).
    const isAnyToastDisplayed = isNudgeVisible || showSuccessToast;
    useEffect(() => {
        updateAppState({ multiPlanToastVisible: isAnyToastDisplayed });
        return () => updateAppState({ multiPlanToastVisible: false });
    }, [isAnyToastDisplayed, updateAppState]);

    if (!groups?.length) return null;

    return (
        <>
            {isNudgeVisible && <MultiPlanNudgeToast onDismiss={dismissNudge} onAccept={openGuide} />}
            <MultiPlanGuideModal isOpen={isModalOpen} onClose={closeGuide} groups={groups} selectedPlans={selectedPlans} onSelectPlan={handleSelectPlan} />
            {showSuccessToast && <MultiPlanSuccessToast appliedPlanCount={appliedPlanCount} onClose={() => setShowSuccessToast(false)} />}
        </>
    );
};

MultiPlanGuide.displayName = "MultiPlanGuide";
